<template>
  <v-app class="bg-main" :style="login_bg_style">
    <div class="login d-flex flex-column">
      <m-snackbar
        id="login-snackbar"
        v-model="alertResultState"
        :type="alertResult.type"
        :timeout="alertResult.timeout || 5000"
        :text="alertResult.stringId" />
      <app-header :hideUserMenu="true" class="flex-grow-0" />

      <v-card v-if="isAuthenticating" class="form-card loading-card mx-auto mt-16" :loading="true">
        <div class="form-card d-flex justify-center align-center">
          {{ $t('common.loading') }}
        </div>
      </v-card>

      <v-container v-else class="d-flex flex-grow-1 align-center">
        <v-card class="form-card" min-width="400" elevation="4">
          <v-card-text v-if="authView === 'signIn'">
            <h2
              :class="[
                'auth-title',
                {'text-center': !isMapitLoginRoute},
                isCognitoLoginEnabled ? 'mb-7' : 'mb-0',
                {'panex-only-title': !isCognitoLoginEnabled},
              ]">
              {{ signInTitle }}
            </h2>

            <template v-if="isCognitoLoginEnabled">
              <v-form ref="signInForm" @submit.prevent="handleSignIn">
                <div class="d-flex flex-column mb-3">
                  <label class="align-center text-left form-row-label">{{ $t('auth.emailLabel') }}</label>
                  <m-email-field
                    v-model="email"
                    :placeholder="$t('auth.emailPlaceholder')"
                    required
                    :required-text="$t('auth.emailRequired')"
                    outlined
                    dense />
                </div>
                <div class="d-flex flex-column mb-1">
                  <label class="align-center text-left form-row-label">{{ $t('auth.passwordLabel') }}</label>
                  <m-text-field
                    v-model="password"
                    :placeholder="$t('auth.passwordPlaceholder')"
                    type="password"
                    required
                    :required-text="$t('auth.passwordRequired')"
                    outlined
                    dense />
                </div>
                <div class="mb-7 text-left">
                  {{ $t('auth.forgotPasswordText') }}
                  <a href="#" @click.prevent="authView = 'forgotPassword'">
                    {{ $t('auth.resetPasswordText') }}
                  </a>
                </div>

                <v-btn type="submit" color="primary" block :loading="loading">
                  {{ $t('auth.signInButton') }}
                </v-btn>
              </v-form>

              <v-divider v-if="isPanexSupportedFlag" class="my-6"></v-divider>
            </template>

            <template v-if="isPanexSupportedFlag">
              <div :class="{'d-flex flex-column align-center': !isCognitoLoginEnabled}">
                <v-divider v-if="!isCognitoLoginEnabled" class="my-6 panex-divider"></v-divider>
                <v-btn
                  color="primary"
                  :block="isCognitoLoginEnabled"
                  :outlined="isCognitoLoginEnabled"
                  class="panex-login-btn"
                  @click="initiatePanexLogin">
                  <span>{{ $t('auth.signInWithPanex') }}</span>
                  <img
                    :src="isCognitoLoginEnabled ? '/images/honda-wordmark.svg' : '/images/honda-wordmark-white.svg'"
                    alt="Honda"
                    class="ml-2 w-100" />
                </v-btn>
              </div>
            </template>
          </v-card-text>

          <v-card-text v-else-if="authView === 'forgotPassword'">
            <h2 class="auth-title mb-7">{{ $t('auth.resetYourPassword') }}</h2>
            <form @submit.prevent="handleForgotPassword">
              <div class="d-flex flex-column mb-3">
                <label class="align-center text-left form-row-label">{{ $t('auth.emailLabel') }} *</label>
                <m-email-field v-model="email" :placeholder="$t('auth.emailPlaceholder')" required outlined dense />
              </div>
              <div class="d-flex justify-space-between align-baseline mt-7">
                <div class="text-center">
                  <a href="#" @click.prevent="authView = 'signIn'" class="text-caption">
                    {{ $t('auth.backToSignIn') }}
                  </a>
                </div>
                <v-btn class="reset-password-button" type="submit" color="primary" block :loading="loading">
                  {{ $t('auth.sendCode') }}
                </v-btn>
              </div>
            </form>
          </v-card-text>

          <v-card-text v-else-if="authView === 'resetPassword'">
            <h2 class="auth-title mb-7">
              {{ challengeSession ? $t('auth.changePassword') : $t('auth.resetYourPassword') }}
            </h2>
            <form @submit.prevent="challengeSession ? handleNewPasswordRequired() : handleResetPassword()">
              <div v-if="!challengeSession" class="d-flex flex-column mb-3">
                <label class="align-center text-left form-row-label">{{ $t('auth.codeLabel') }}</label>
                <m-text-field
                  v-model="confirmationCode"
                  :placeholder="$t('auth.codePlaceholder')"
                  required
                  outlined
                  dense />
              </div>
              <div class="d-flex flex-column mb-3">
                <label class="align-center text-left form-row-label">{{ $t('auth.newPasswordLabel') }}</label>
                <m-text-field
                  v-model="newPassword"
                  :placeholder="$t('auth.newPasswordPlaceholder')"
                  type="password"
                  required
                  outlined
                  dense />
              </div>
              <div class="d-flex justify-space-between align-baseline mt-7">
                <div class="text-center">
                  <a href="#" @click.prevent="resetToSignIn" class="text-caption">
                    {{ $t('auth.backToSignIn') }}
                  </a>
                </div>
                <v-btn class="reset-password-button" type="submit" color="primary" block :loading="loading">
                  {{ $t('auth.submit') }}
                </v-btn>
              </div>
            </form>
          </v-card-text>
        </v-card>
      </v-container>

      <v-footer fixed bottom dark color="transparent">
        <div class="full-width text-center">Copyright&copy; {{ new Date().getFullYear() }} MAPIT IoT S.L.</div>
      </v-footer>
    </div>
  </v-app>
</template>

<script>
import '../styles/login.less'

import {fetchAuthSession} from 'aws-amplify/auth'
import {Hub} from 'aws-amplify/utils'

import AppHeader from '@/components/AppHeader'
import {eventBus} from '@/main'
import {authAdapter} from '@/services/auth-adapter'
import {createCompatibleUser} from '@/utils/user-compat'

export default {
  components: {AppHeader},
  name: 'Login',
  data() {
    return {
      authView: 'signIn',
      email: '',
      password: '',
      confirmationCode: '',
      newPassword: '',
      loading: false,
      isAuthenticating: false,
      bgNumber: null,
      challengeSession: null, // Store challenge session for NEW_PASSWORD_REQUIRED
    }
  },
  async mounted() {
    window.document.body.className = 'styled_forms'
    eventBus.$on('access-denied', this.handleAccessDenied)

    // Add noindex meta tag for /mapit-login to prevent search engine indexing
    if (this.$route.meta?.forceCognitoOnly) {
      const meta = document.createElement('meta')
      meta.name = 'robots'
      meta.content = 'noindex, nofollow'
      meta.id = 'mapit-login-noindex'
      document.head.appendChild(meta)
    }

    const urlParams = new URLSearchParams(window.location.search)

    // Check if this is an OIDC callback (has code and state in URL)
    if (urlParams.has('code') && urlParams.has('state')) {
      this.handleAuthCallback()
      return
    }

    // Auto-trigger Panex login if accessed via /auth/callback without auth params
    const isAuthCallbackRoute = this.$route.query._fromAuthCallback === 'true'

    if (!isAuthCallbackRoute) return

    // Clean up the flags from URL
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {_fromAuthCallback, _bg, ...cleanQuery} = this.$route.query
    this.$router.replace({query: cleanQuery})

    // Clear any existing session to ensure fresh login
    const isAuthenticated = await authAdapter.isAuthenticated()

    if (isAuthenticated) {
      try {
        await this.$store.dispatch('logout')
      } catch (error) {
        console.warn('Error during logout before Panex login:', error)
        // Continue anyway to initiate new login
      }
    }

    await this.initiatePanexLogin()
  },
  beforeDestroy() {
    eventBus.$off('access-denied', this.handleAccessDenied)

    // Remove noindex meta tag if it was added
    const metaTag = document.getElementById('mapit-login-noindex')
    if (metaTag) {
      metaTag.remove()
    }
  },
  created() {
    // Get background image from Vue Router query (not window.location.search)
    const bgFromQuery = this.$route.query._bg
    this.bgNumber = bgFromQuery ? parseInt(bgFromQuery) : Math.ceil(Math.random() * 3)

    // Check if coming from auth callback to set loading state immediately
    const urlParams = new URLSearchParams(window.location.search)
    const isFromAuthCallback = urlParams.get('_fromAuthCallback') === 'true'
    const hasOidcParams = urlParams.has('code') && urlParams.has('state')

    this.isAuthenticating = isFromAuthCallback || hasOidcParams

    Hub.listen('auth', ({payload}) => {
      if (payload.event === 'signIn') {
        this.$router.push({name: 'Dashboard'}).catch(() => {
          // Suppress navigation errors (e.g., when access is denied by guard)
        })
      }
    })
  },
  computed: {
    login_bg_style() {
      return {
        background: `url('/images/login-bg-${this.bgNumber}.png')`,
      }
    },
    isMapitLoginRoute() {
      return this.$route.meta?.forceCognitoOnly === true
    },
    isPanexSupportedFlag() {
      // Hide Panex login on /mapit-login route (Cognito-only mode)
      return !this.isMapitLoginRoute
    },
    isCognitoLoginEnabled() {
      // Always enable Cognito login on /mapit-login route
      if (this.isMapitLoginRoute) {
        return true
      }
      return process.env.VUE_APP_IS_COGNITO_LOGIN_ENABLED === 'true'
    },
    signInTitle() {
      return this.isCognitoLoginEnabled ? this.$t('auth.signInHeaderText') : this.$t('auth.welcomeToPortal')
    },
    alertResult() {
      return this.$store.getters.getAlertResult
    },
    alertResultState: {
      get() {
        return this.alertResult.open
      },
      set(value) {
        this.$store.dispatch('setAlertResult', {...this.alertResult, open: value})
      },
    },
  },
  methods: {
    getErrorMessage(error) {
      const errorMessage = error?.message || JSON.stringify(error)

      // Check if it's a 502 Bad Gateway error
      if (
        error?.response?.status === 502 ||
        error?.status === 502 ||
        errorMessage.includes('502') ||
        errorMessage.toLowerCase().includes('bad gateway')
      ) {
        return this.$t('auth.badGatewayError')
      }

      // Map common authentication errors to translated messages
      if (errorMessage.includes('username is required') || errorMessage.includes('Username is required')) {
        return this.$t('auth.usernameRequiredForReset')
      }

      if (errorMessage.includes('Username/client id combination not found')) {
        return this.$t('auth.userNotFound')
      }

      if (errorMessage.includes('Invalid verification code')) {
        return this.$t('auth.invalidVerificationCode')
      }

      if (errorMessage.includes('Attempt limit exceeded')) {
        return this.$t('auth.attemptLimitExceeded')
      }

      if (
        errorMessage.includes('Password does not conform to policy') &&
        errorMessage.includes('Password not long enough')
      ) {
        return this.$t('auth.passwordTooShort')
      }

      // Return the original error message if no mapping found
      return errorMessage
    },

    async handleAuthCallback() {
      this.isAuthenticating = true
      try {
        const user = await authAdapter.handleCallback()

        // Tokens are automatically stored by UserManager in localStorage
        const tokensReceived = ['ID token']
        if (user.access_token) tokensReceived.push('access token')
        if (user.refresh_token) tokensReceived.push('refresh token')

        await fetchAuthSession()

        // Preload user attributes into store to avoid redirect loop
        // This ensures the router guard will see the user immediately
        const userAttributes = await authAdapter.fetchUserAttributes()
        const session = await authAdapter.getCurrentSession()

        // Create a v3-compatible user object for the store
        const compatibleUser = createCompatibleUser(userAttributes, session)

        // Store the cognito user in Vuex immediately
        this.$store.commit('SET_COGNITO_USER', compatibleUser)

        // Set flag for Dashboard to show login success message
        sessionStorage.setItem('showLoginSuccess', 'true')

        // Get return URL from OIDC state or default to home
        const returnUrl = user.state || '/'

        // Navigate to return URL
        // Keep isAuthenticating = true to maintain loading modal during navigation
        this.$router.push(returnUrl).catch(() => {
          // Navigation was redirected or cancelled, but that's ok
        })
      } catch (error) {
        // If there's no matching state in storage, the user arrived from the OIDC host
        // portal without this app having initiated the flow. Start a fresh OIDC login
        // so our app creates its own state and goes through the proper redirect.
        if (error?.message?.includes('No state in response')) {
          window.history.replaceState({}, document.title, window.location.pathname)
          await this.initiatePanexLogin()
          return
        }

        console.error('Authentication callback error:', error)
        const message = this.getErrorMessage(error)

        this.showError(message)
        this.isAuthenticating = false

        setTimeout(() => {
          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname)
        }, 2000)
      }
    },

    async handleSignIn() {
      // Trigger Vuetify form validation
      const isValid = this.$refs.signInForm?.validate()

      if (!isValid) {
        return
      }

      this.loading = true
      this.isAuthenticating = true
      try {
        const result = await authAdapter.signInWithCredentials(this.email, this.password)

        // In Amplify v6, successful sign in returns result with isSignedIn
        if (result.isSignedIn) {
          // Set flag for Dashboard to show login success message
          sessionStorage.setItem('showLoginSuccess', 'true')
          // Navigate directly to dashboard on successful sign in
          // Keep isAuthenticating = true to maintain loading modal during navigation
          this.$router.push({name: 'Dashboard'}).catch(() => {
            // Navigation was redirected or cancelled, but that's ok
          })
        } else {
          // Handle authentication challenges
          if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
            // User needs to set a new password (first login or admin reset)
            this.challengeSession = result
            this.newPassword = ''
            this.authView = 'resetPassword'
            this.isAuthenticating = false
          } else if (result.nextStep?.signInStep === 'RESET_PASSWORD') {
            // User needs to reset password via email
            this.confirmationCode = ''
            this.newPassword = ''
            this.authView = 'forgotPassword'
            this.showError(this.$t('auth.passwordResetRequired'))
            this.isAuthenticating = false
          } else {
            // Other challenges - show loading (this shouldn't happen normally)
            console.warn('Unhandled sign-in challenge:', result.nextStep?.signInStep)
            this.isAuthenticating = true
          }
        }
      } catch (err) {
        console.error('Sign in error:', err)

        // Handle case where user is already signed in (e.g., from another tab)
        if (err?.message?.includes('There is already a signed in user')) {
          // Redirect to dashboard instead of showing error
          this.$router.push({name: 'Dashboard'})
          return
        }

        if (err?.code === 'PasswordResetRequiredException') {
          this.confirmationCode = ''
          this.newPassword = ''
          this.authView = 'forgotPassword'
          this.showError(this.$t('auth.passwordResetRequired'))
        } else {
          const errorMessage = err.message || JSON.stringify(err)
          // Remove trailing punctuation for translation key
          const cleanMessage = errorMessage.replace(/[.!?]+$/, '')
          this.showError(`auth.${cleanMessage}`, errorMessage)
        }
        this.isAuthenticating = false
      } finally {
        this.loading = false
      }
    },

    async handleForgotPassword() {
      this.loading = true
      try {
        await authAdapter.resetPassword(this.email)
        this.authView = 'resetPassword'
        this.showSuccess(this.$t('auth.codeSent'))
      } catch (err) {
        console.error('Forgot password error:', err)
        this.showError(this.getErrorMessage(err))
      } finally {
        this.loading = false
      }
    },

    async handleResetPassword() {
      this.loading = true
      try {
        await authAdapter.confirmResetPassword(this.email, this.confirmationCode, this.newPassword)
        this.showSuccess(this.$t('auth.passwordResetSuccess'))
        this.authView = 'signIn'
        this.confirmationCode = ''
        this.newPassword = ''
      } catch (err) {
        console.error('Reset password error:', err)
        this.showError(this.getErrorMessage(err))
      } finally {
        this.loading = false
      }
    },

    async handleNewPasswordRequired() {
      this.loading = true
      this.isAuthenticating = true
      try {
        console.log('Attempting to confirm sign in with new password...')
        console.log('Challenge session:', this.challengeSession)

        // Extract required attributes from the challenge if present
        let userAttributes = undefined
        if (this.challengeSession?.nextStep?.missingAttributes?.length > 0) {
          console.log('Required attributes:', this.challengeSession.nextStep.missingAttributes)
          // If there are required attributes, we might need to collect them from the user
          // For now, we'll try without them since requiredAttributes was empty in your case
        }

        // Complete the NEW_PASSWORD_REQUIRED challenge using authAdapter
        const result = await authAdapter.confirmSignInWithNewPassword(this.newPassword, userAttributes)

        console.log('confirmSignIn result:', result)

        if (result.isSignedIn) {
          this.showSuccess(this.$t('auth.passwordResetSuccess'))
          // Set flag for Dashboard to show login success message
          sessionStorage.setItem('showLoginSuccess', 'true')
          // Clear challenge session
          this.challengeSession = null
          this.newPassword = ''
          // Navigate to dashboard
          // Keep isAuthenticating = true to maintain loading modal during navigation
          this.$router.push({name: 'Dashboard'}).catch(() => {
            // Navigation was redirected or cancelled, but that's ok
          })
        } else {
          // Unexpected - another challenge after password change
          console.warn('Unexpected challenge after password reset:', result.nextStep)
          this.showError(this.$t('auth.unexpectedError'))
          this.isAuthenticating = false
        }
      } catch (err) {
        console.error('New password required error:', err)
        console.error('Error details:', JSON.stringify(err, null, 2))

        const errorMessage = err.message || JSON.stringify(err)

        // Check for session expired error
        if (err.name === 'NotAuthorizedException' || errorMessage.includes('session is expired')) {
          this.showError(this.$t('auth.sessionExpired'))
          // Reset to sign in view so user can try again
          this.resetToSignIn()
        } else if (
          errorMessage.includes('Password does not conform to policy') &&
          errorMessage.includes('Password not long enough')
        ) {
          this.showError('auth.passwordTooShort')
        } else {
          this.showError(errorMessage)
        }
        this.isAuthenticating = false
      } finally {
        this.loading = false
      }
    },

    async initiatePanexLogin() {
      try {
        await authAdapter.signInWithOIDC()
      } catch (error) {
        console.error('Panex OIDC error:', error)
        this.showError('Failed to initiate Panex login')
      }
    },

    handleAccessDenied() {
      this.isAuthenticating = false
    },

    resetToSignIn() {
      this.authView = 'signIn'
      this.challengeSession = null
      this.newPassword = ''
      this.confirmationCode = ''
    },

    showError(keyOrMessage, defaultMessage = null) {
      // Try to translate as key first, fallback to defaultMessage or the key itself
      const finalMessage = this.$te(keyOrMessage) ? this.$t(keyOrMessage) : defaultMessage || keyOrMessage

      this.$store.dispatch('setAlertResult', {
        type: 'error',
        stringId: finalMessage,
        open: true,
      })
    },

    showSuccess(message) {
      this.$store.dispatch('setAlertResult', {
        type: 'success',
        stringId: message,
        open: true,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
#login-snackbar {
  top: 70px;
  z-index: 0;
}

.form-card {
  padding: 20px;
}

.loading-card {
  padding-top: 0;
  width: 160px;
  height: 100px;
}

.card-title {
  text-align: center;
  color: black;
  font-size: 24px !important;
  font-family: 'ProximaNovaExCnSbold', sans-serif !important;
  text-transform: uppercase;
}

.auth-title {
  color: #152939;
  font-weight: 500;
  font-size: 20px;
  text-align: left;
}

.form-row-label {
  color: #111111;
}

.reset-password-button {
  min-width: inherit !important;
  flex-grow: 0;
}

.panex-divider {
  width: 100%;
}

.panex-login-btn {
  border-radius: 4px;
}

.panex-only-title {
  font-size: 16px;
}
</style>
